import React from 'react';
import { FileText, Calendar, CheckCircle2, ExternalLink, Briefcase } from 'lucide-react';
import { motion } from 'framer-motion';

export const TaxFiling: React.FC = () => {
  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-8"
      >
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="p-3 rounded-xl bg-emerald-500/10">
            <FileText className="w-10 h-10 text-emerald-500" />
          </div>
          <h2 className="text-4xl font-bold">Tax Filing Options</h2>
        </div>
        <p className="text-slate-400 text-lg">
          Choose the tax filing solution that works best for you
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Self-Filing Option */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="glass-card p-8 border border-emerald-500/20"
        >
          <h3 className="text-2xl font-semibold mb-4">Option 1: Self-Filing with TurboTax</h3>
          <p className="text-slate-300 mb-6">
            File your taxes independently using our trusted partner TurboTax. Get special member discounts and step-by-step guidance.
          </p>
          <a
            href="https://refer.intuit.com/drewmaster35"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors font-medium"
          >
            File Taxes Now
            <ExternalLink className="w-5 h-5" />
          </a>
        </motion.div>

        {/* Professional Services Option */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="glass-card p-8 border border-emerald-500/20"
        >
          <h3 className="text-2xl font-semibold mb-4">Option 2: Professional Tax Services</h3>
          <p className="text-slate-300 mb-6">
            Work with our certified tax expert, Donald Harrell, for personalized tax preparation and maximized returns.
          </p>

          <div className="mb-6 p-4 bg-emerald-500/5 rounded-lg border border-emerald-500/20">
            <div className="flex items-start gap-4">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <Briefcase className="w-6 h-6 text-emerald-500" />
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-2">Donald Harrell</h4>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Certified Public Accountant
                  </li>
                  <li className="flex items-center gap-2 text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    15+ years of tax preparation experience
                  </li>
                  <li className="flex items-center gap-2 text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Specializing in personal and small business taxes
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <a
            href="https://calendly.com/donaldharrell/tax-consultation"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors font-medium"
          >
            <Calendar className="w-5 h-5" />
            Book a Free Consultation
          </a>
        </motion.div>
      </div>

      {/* Common Benefits */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="glass-card p-8 border border-emerald-500/20"
      >
        <h3 className="text-xl font-semibold mb-6">Both options include:</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            'Secure document handling',
            'Maximum refund guarantee',
            'Year-round tax support',
            'Direct deposit for faster returns'
          ].map((benefit, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-4 bg-emerald-500/5 rounded-lg border border-emerald-500/20"
            >
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              <span className="text-slate-300">{benefit}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};